import { Control, Controller } from 'react-hook-form';
import { useState } from 'react';
import {
  GmailDeletionFormValues,
  searchOptions,
} from '../gmail-deletion-form-page';
import { SectionHeader } from '../../../../shared/section-header';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Link from '@mui/material/Link';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

interface StepConfigurationProps {
  control: Control<GmailDeletionFormValues>;
}

export default function StepConfiguration({ control }: StepConfigurationProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl && anchorEl === event.currentTarget) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popOverId = open ? 'email-filter-info-popover' : undefined;

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
      }}
    >
      <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
        Define specific criteria and filters to generate a report of emails that
        match this criteria. You can then import the report in a separate step
        to execute the deletion process, removing all emails that are present in
        the report.
      </Typography>
      <SectionHeader>Set Search</SectionHeader>
      <Stack gap={2} sx={{ pt: 2 }}>
        <Stack flexDirection="row" gap={4}>
          <Controller
            name="whereToSearch"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Search"
                sx={{ width: 600 }}
                value={field.value || ''}
                onChange={field.onChange}
              >
                {searchOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} mt={4}>
        <SectionHeader>
          Set Email Filters{' '}
          <IconButton
            aria-describedby={popOverId}
            onClick={handleInfoClick}
            size="small"
            sx={{ p: 0.5 }}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
          <Popover
            id={popOverId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box
              sx={{
                p: 2,
                width: 600,
              }}
              gap={2}
            >
              <Typography variant="body2" fontWeight="bold">
                Advanced Search Operators
              </Typography>
              <Typography variant="body2">
                For more precise filtering, you can use advanced search
                operators in any of the fields.
                <ul style={{ marginTop: 0 }}>
                  <li>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight="bold"
                    >
                      AND:
                    </Typography>{' '}
                    Use AND (or just a space) to find emails that contain all
                    specified terms. Example: report AND finance
                  </li>
                  <li>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight="bold"
                    >
                      OR:
                    </Typography>
                    Use OR to find emails that contain any of the specified
                    terms. Example: (project OR task)
                  </li>
                  <li>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight="bold"
                    >
                      - (Minus Sign):
                    </Typography>
                    Use - before a word to exclude emails containing that word.
                    Example: meeting -urgent
                  </li>
                  <li>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight="bold"
                    >
                      " " (Quotation Marks):
                    </Typography>
                    Use quotation marks for an exact phrase match. Example:
                    "your order has shipped"
                  </li>
                  <li>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight="bold"
                    >
                      has:attachment:
                    </Typography>
                    Can be used in "Has the words" to specifically look for
                    attachments. Example: report has:attachment
                  </li>
                  <li>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight="bold"
                    >
                      filename:
                    </Typography>
                    Filter by attachment filename. Example: filename:resume.pdf
                  </li>
                  <li>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight="bold"
                    >
                      is:important, is:starred, is:unread, is:read:
                    </Typography>
                    Filter by importance, star status, or read status.
                  </li>
                  <li>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight="bold"
                    >
                      label:
                    </Typography>{' '}
                    Filter by existing labels. Example: label:Work
                  </li>
                </ul>
              </Typography>
              <Typography variant="body2" textAlign="end">
                <Link
                  href="https://support.google.com/mail/answer/7190?sjid=12958360836706136756-NA"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here
                </Link>{' '}
                for a complete list of Gmail filtering operators
              </Typography>
            </Box>
          </Popover>
        </SectionHeader>
      </Stack>
      <Stack gap={2} sx={{ pt: 2 }}>
        <Stack flexDirection="row" gap={4}>
          <Controller
            name="from"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                autoComplete="off"
                label="From"
                sx={{ width: 600 }}
                helperText="Emails sent from a specific email address or domain."
              />
            )}
          />
        </Stack>
        <Stack flexDirection="row" gap={4}>
          <Controller
            name="to"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'to',
                }}
                InputLabelProps={{
                  htmlFor: 'to',
                }}
                label="To"
                sx={{ width: 600 }}
                helperText="Emails sent to a specific email address."
              />
            )}
          />
        </Stack>
        <Stack flexDirection="row" gap={4}>
          <Controller
            name="subject"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                autoComplete="off"
                label="Subject"
                sx={{ width: 600 }}
                helperText="Enter words or phrases that appear in the email's subject line."
              />
            )}
          />
        </Stack>
        <Stack flexDirection="row" gap={4}>
          <Controller
            name="hasTheWords"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Has the words"
                sx={{ width: 600 }}
                helperText="Enter keywords or phrases that appear anywhere in the email body."
              />
            )}
          />
        </Stack>
        <Stack flexDirection="row" gap={4}>
          <Controller
            name="doesntHaveTheWords"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Doesn't have"
                sx={{ width: 600 }}
                helperText="Enter keywords or phrases that should NOT appear in the email."
              />
            )}
          />
        </Stack>
      </Stack>
      <SectionHeader sx={{ mt: 4 }}>Set Deletion Type</SectionHeader>
      <Stack gap={2} sx={{ pt: 1, pl: 2 }}>
        <Stack flexDirection="row" gap={4}>
          <Controller
            name={'type'}
            control={control}
            render={({ field: props }) => {
              return (
                <FormControl>
                  <RadioGroup
                    aria-labelledby="radio-buttons-group-label"
                    name="radio-buttons-group"
                    value={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    row
                  >
                    <FormControlLabel
                      value="message"
                      control={<Radio />}
                      label="By Message"
                    />
                    <FormControlLabel
                      value="thread"
                      control={<Radio />}
                      label="By Thread"
                    />
                  </RadioGroup>
                </FormControl>
              );
            }}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
